<template>
  <div class="form-element" :id="element.id">
    <component
      :is="getElementComponent(element.type)"
      v-bind="getElementProps(element)"
      :isAutoFilled="element.type === 'configurator-choice' ? isAutoFilled : undefined"
      @update:modelValue="(value) => handleInput(element, value)"
      @update:quantity="(data) => handleQuantityUpdate(element, data)"
      @blur="handleBlur(element.id)"
      @dataEnriched="handleDataEnriched"
      :formId="formId"
      :fieldId="element.id"
      :key="element.id"
      :formData="formData"
      :form="form"
      :submissionId="submissionId"
    />
    <div class="form-element__error" v-if="errors">
      {{ errors }}
    </div>
  </div>
</template>

<script>
  import { useRoute } from 'vue-router';
  import AddressElement from './elements/AddressElement.vue';
  import DateElement from './elements/DateElement.vue';
  import EmailElement from './elements/EmailElement.vue';
  import HtmlElement from './elements/HtmlElement.vue';
  import SingleChoiceElement from './elements/SingleChoiceElement.vue';
  import MultiChoiceElement from './elements/MultiChoiceElement.vue';
  import PictureChoiceElement from './elements/PictureChoiceElement.vue';
  import ShortAnswerElement from './elements/ShortAnswerElement.vue';
  import LongAnswerElement from './elements/LongAnswerElement.vue';
  import NameElement from './elements/NameElement.vue';
  import PhoneElement from './elements/PhoneElement.vue';
  import NumberElement from './elements/NumberElement.vue';
  import SliderElement from './elements/SliderElement.vue';
  import StarRatingElement from './elements/StarRatingElement.vue';
  import FileUploadElement from './elements/FileUploadElement.vue';
  import PhotoDrawingElement from './elements/PhotoDrawingElement.vue';
  import HeadingElement from './elements/HeadingElement.vue';
  import TextElement from './elements/TextElement.vue';
  import SchedulingElement from './elements/SchedulingElement.vue';
  import FormulaElement from './elements/FormulaElement.vue';
  import SwitchElement from './elements/SwitchElement.vue';
  import TermsAcceptanceElement from './elements/TermsAcceptanceElement.vue';
  import MarketingOptInElement from './elements/MarketingOptInElement.vue';
  import KvkElement from './elements/KvkElement.vue';
  import CompanyNameElement from './elements/CompanyNameElement.vue';
  import HiddenElement from './elements/HiddenElement.vue';
  import ThankYouElement from './elements/ThankYouElement.vue';
  import SubmissionDataElement from './elements/SubmissionDataElement.vue';
  import ButtonElement from './elements/ButtonElement.vue';
  import ProductAdviseElement from './elements/ProductAdviseElement.vue';
  import ProductSelectionElement from './elements/ProductSelectionElement.vue';
  import ConfiguratorChoiceElement from './elements/ConfiguratorChoiceElement.vue';
  import NoticeElement from './elements/NoticeElement.vue';
  import ImageElement from './elements/ImageElement.vue';
  // import CPQQuoteSummaryElement from './elements/CPQQuoteSummaryElement.vue';
  import { useFormDefaults } from '@shared/composables/useFormDefaults';
  import { useDynamicDefaults } from '@shared/composables/useDynamicDefaults';

  export default {
    name: 'FormElement',

    components: {
      AddressElement,
      DateElement,
      EmailElement,
      SingleChoiceElement,
      MultiChoiceElement,
      PictureChoiceElement,
      ShortAnswerElement,
      LongAnswerElement,
      NameElement,
      PhoneElement,
      NumberElement,
      SliderElement,
      StarRatingElement,
      FileUploadElement,
      PhotoDrawingElement,
      HeadingElement,
      TextElement,
      HtmlElement,
      SchedulingElement,
      FormulaElement,
      SwitchElement,
      TermsAcceptanceElement,
      MarketingOptInElement,
      KvkElement,
      CompanyNameElement,
      HiddenElement,
      ThankYouElement,
      SubmissionDataElement,
      ButtonElement,
      ProductAdviseElement,
      ProductSelectionElement,
      ConfiguratorChoiceElement,
      NoticeElement,
      ImageElement,
    },

    props: {
      element: {
        type: Object,
        required: true,
      },
      formData: {
        type: Object,
        required: true,
      },
      isAutoFilled: {
        type: Boolean,
        default: false,
      },
      formId: {
        type: String,
        required: true,
      },
      errors: {
        type: String,
        default: null,
      },
      touched: {
        type: Object,
        default: () => ({}),
      },
      dummy: {
        type: Boolean,
        default: false,
      },
      formSettings: {
        type: Object,
        default: () => ({}),
      },
      form: {
        type: Object,
        default: () => ({}),
      },
      submissionId: {
        type: [String, null],
        default: null,
      },
    },

    emits: ['update:formData', 'field-blur', 'element-data-enriched'],

    setup(props, { emit }) {
      const route = useRoute();
      const { getDefaultValue } = useFormDefaults(route?.query || {});
      const { isDynamicValue } = useDynamicDefaults();

      const getElementComponent = (type) => {
        const components = {
          address: AddressElement,
          date: DateElement,
          email: EmailElement,
          'single-choice': SingleChoiceElement,
          'multiple-choice': MultiChoiceElement,
          'picture-choice': PictureChoiceElement,
          name: NameElement,
          number: NumberElement,
          phone: PhoneElement,
          'short-answer': ShortAnswerElement,
          'long-answer': LongAnswerElement,
          heading: HeadingElement,
          text: TextElement,
          html: HtmlElement,
          scheduling: SchedulingElement,
          slider: SliderElement,
          'star-rating': StarRatingElement,
          'file-upload': FileUploadElement,
          'photo-drawing': PhotoDrawingElement,
          switch: SwitchElement,
          'terms-acceptance': TermsAcceptanceElement,
          'marketing-opt-in': MarketingOptInElement,
          formula: FormulaElement,
          kvk: KvkElement,
          'company-name': CompanyNameElement,
          hidden: HiddenElement,
          'thank-you': ThankYouElement,
          'submission-data': SubmissionDataElement,
          button: ButtonElement,
          'product-advise': ProductAdviseElement,
          'product-selection': ProductSelectionElement,
          'configurator-choice': ConfiguratorChoiceElement,
          notice: NoticeElement,
          image: ImageElement,
        };
        return components[type] || ShortAnswerElement;
      };

      const getElementProps = (element) => {
        let modelValue =
          element.type === 'switch' ||
          element.type === 'terms-acceptance' ||
          element.type === 'marketing-opt-in'
            ? props.formData[element.id] === true || props.formData[element.id] === 'true'
            : props.formData[element.id];

        // For upsell elements: fallback to normalized key and filter by available choices
        if (element.type === 'configurator-choice' && element.props?.upsellCardMode) {
          // If no value for this specific element ID, try the normalized key
          if (!modelValue || (Array.isArray(modelValue) && modelValue.length === 0)) {
            const normalizedKey = element.props?.name; // name is the normalized key
            const normalizedValue = props.formData[normalizedKey];

            if (Array.isArray(normalizedValue) && normalizedValue.length > 0) {
              // Filter to only include upsells that are choices in this element
              const choiceIds = element.props?.choices?.map((c) => c.id) || [];
              modelValue = normalizedValue.filter((upsellItem) => {
                const upsellId = typeof upsellItem === 'object' ? upsellItem.id : upsellItem;
                return choiceIds.includes(upsellId);
              });
            }
          }
        }

        // For upsells: convert old format (array of IDs + separate quantity fields) to new format (array of objects)
        if (
          element.type === 'configurator-choice' &&
          element.props?.upsellCardMode &&
          Array.isArray(modelValue)
        ) {
          // Check if it's already new format (array of objects)
          if (modelValue.length === 0 || typeof modelValue[0] !== 'object' || !modelValue[0].id) {
            // Old format: convert array of IDs to array of objects
            const upsellIds = modelValue;
            modelValue = upsellIds.map((id) => {
              const quantityFieldId = `${element.id}_${id}_quantity`;
              const quantity = props.formData[quantityFieldId];
              return {
                id: id,
                quantity: quantity !== undefined && quantity !== null ? Number(quantity) : 1,
              };
            });
          }
        }

        return {
          ...element.props,
          modelValue,
          error: props.errors ? props.errors[element.id] : null,
          touched: props.touched[element.id],
          formId: props.formId,
          fieldId: element.id,
          element: element,
          formData: props.formData,
          form: props.form || {},
          key: `${element.id}-${modelValue}-${JSON.stringify(element.props)}`,
          parameters: props.formSettings?.parameters || [],
        };
      };

      const handleInput = (element, value) => {
        emit('update:formData', {
          [element.id]: value,
        });
      };

      const handleQuantityUpdate = (element, data) => {
        // Store quantity in formData with fieldName as key
        emit('update:formData', {
          [data.fieldName]: data.quantity,
        });
      };

      const handleBlur = (elementId) => {
        emit('field-blur', elementId);
      };

      const handleDataEnriched = (data) => {
        emit('element-data-enriched', { element: props.element, data });
      };

      return {
        getElementComponent,
        getElementProps,
        handleInput,
        handleQuantityUpdate,
        handleBlur,
        handleDataEnriched,
      };
    },
  };
</script>

<style scoped lang="scss">
  .form-element {
    // margin-bottom: 0.5rem;
    padding: 0.75rem 0;

    &:last-child {
      margin-bottom: 0;
    }

    // Add error state styling if needed
    &--error {
      border-color: var(--error-500);
    }

    // Add touched state styling if needed
    &--touched {
      // Your styles here
    }

    &__error {
      color: var(--red-500);
      font-size: 0.875rem;
      margin-top: 0.75rem;
    }
  }
</style>
